// Rating.js - Main Component
import React, { useEffect, useState } from 'react';
import { 
    Box, 
    Typography, 
    Paper, 
    Container,
    Fade,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    CardHeader,
    Skeleton,
    Stack,
    Divider,
    alpha,
    Avatar,
    Chip,
    Grid
} from '@mui/material';
import { 
    AssignmentTurnedIn as SelfReflectionIcon,
    School as StudentIcon,
    SupervisorAccount as MentorIcon,
    Timeline as ProgressIcon,
    Analytics as AnalyticsIcon,
    Create as CreateIcon
} from '@mui/icons-material';
import RatingForm from './Mentor/RatingForm';
import RatingFormsTable from './Mentor/RatingFormsTable';
import RatingFormsForUser from './Self/RatingFormsForUser';

const Rating = () => {
    const [refresh, setRefresh] = useState(false);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleRefresh = () => {
        setRefresh((prevRefresh) => !prevRefresh);
    };

    useEffect(() => {
        // Get user role and handle potential null/undefined values
        const role = localStorage.getItem("userRole");
        setUserRole(role);
        
        // Simulate loading for smooth transition
        const timer = setTimeout(() => {
            setLoading(false);
        }, 300);
        
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (refresh) {
            // Reset refresh state after handling
            const timer = setTimeout(() => setRefresh(false), 100);
            return () => clearTimeout(timer);
        }
    }, [refresh]);

    // Enhanced loading component
    const LoadingComponent = () => (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Paper 
                elevation={0}
                sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha('#0a0068', 0.1)} 0%, ${alpha('#0c0082', 0.1)} 100%)`,
                    border: `1px solid ${alpha('#0a0068', 0.2)}`
                }}
            >
                <Box display="flex" flexDirection="column" alignItems="center" spacing={3}>
                    <Skeleton 
                        variant="rectangular" 
                        width="100%" 
                        height={120} 
                        sx={{ borderRadius: 2, mb: 3 }} 
                    />
                    <Stack spacing={2} width="100%">
                        <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 2 }} />
                        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2 }} />
                    </Stack>
                </Box>
            </Paper>
        </Container>
    );

    // Enhanced header component
    const HeaderSection = () => {
        const roleConfig = {
            Mentor: {
                icon: <MentorIcon sx={{ fontSize: 32 }} />,
                title: "Self-Reflection Management Hub",
                subtitle: "Create, track, and manage student performance self-reflections",
                color: "#0a0068",
                chipLabel: "Mentor Dashboard"
            },
            default: {
                icon: <StudentIcon sx={{ fontSize: 32 }} />,
                title: "My Performance Journey",
                subtitle: "View your evaluations and track your progress",
                color: "#0c0082",
                chipLabel: "Student Portal"
            }
        };

        const config = roleConfig[userRole] || roleConfig.default;

        return (
            <Paper 
                elevation={4}
                sx={{ 
                    p: { xs: 2, md: 3 }, 
                    mb: 3, 
                    background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)`,
                    color: 'white',
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} md="auto">
                        <Avatar 
                            sx={{ 
                                width: 56, 
                                height: 56, 
                                bgcolor: alpha('#ffffff', 0.2),
                                backdropFilter: 'blur(10px)',
                                border: `2px solid ${alpha('#ffffff', 0.3)}`
                            }}
                        >
                            {config.icon}
                        </Avatar>
                    </Grid>
                    <Grid item xs={12} md>
                        <Stack spacing={1}>
                            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <Typography 
                                    variant={isMobile ? "h6" : "h5"} 
                                    component="h1" 
                                    fontWeight="bold"
                                    sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }}
                                    sx={{ 
                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        letterSpacing: '-0.02em'
                                    }}
                                >
                                    {config.title}
                                </Typography>
                                <Chip 
                                    label={config.chipLabel}
                                    size="small"
                                    sx={{ 
                                        bgcolor: alpha('#ffffff', 0.2),
                                        color: 'white',
                                        fontWeight: 600,
                                        backdropFilter: 'blur(10px)',
                                        border: `1px solid ${alpha('#ffffff', 0.3)}`
                                    }}
                                />
                            </Box>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    fontSize: '0.8rem',
                                    opacity: 0.95,
                                    fontWeight: 400,
                                    lineHeight: 1.4
                                }}
                            >
                                {config.subtitle}
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>
        );
    };

    // Enhanced card wrapper component
    const EnhancedCard = ({ title, icon, children, delay = 0 }) => (
        <Fade in={true} timeout={600} style={{ transitionDelay: `${delay}ms` }}>
            <Card 
                elevation={0}
                sx={{ 
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    background: alpha('#ffffff', 0.9),
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease-in-out',
                    // '&:hover': {
                    //     transform: 'translateY(-2px)',
                    //     boxShadow: `0 8px 25px ${alpha('#0a0068', 0.15)}`,
                    //     borderColor: alpha('#0a0068', 0.3),
                    // }
                }}
            >
                <CardHeader
                    avatar={
                        <Avatar 
                            sx={{ 
                                bgcolor: alpha('#0a0068', 0.1),
                                color: '#0a0068',
                                width: 48,
                                height: 48
                            }}
                        >
                            {icon}
                        </Avatar>
                    }
                    title={
                        <Typography 
                            variant="h6" 
                            component="h2" 
                            fontWeight="bold"
                            sx={{ fontSize: '0.9rem' }}
                            color="primary"
                        >
                            {title}
                        </Typography>
                    }
                    sx={{ 
                        pb: 1,
                        '& .MuiCardHeader-content': {
                            overflow: 'visible'
                        }
                    }}
                />
                <Divider sx={{ mx: 2 }} />
                <CardContent sx={{ pt: 3 }}>
                    {children}
                </CardContent>
            </Card>
        </Fade>
    );

    if (loading) {
        return <LoadingComponent />;
    }

    return (
        <Box 
            sx={{ 
                minHeight: '100vh',
                background: `linear-gradient(135deg, ${alpha('#f8f9fa', 1)} 0%, ${alpha('#e9ecef', 0.5)} 100%)`,
                py: 3
            }}
        >
            <Container maxWidth="xl">
                <Fade in={true} timeout={800}>
                    <Box>
                        {/* <HeaderSection /> */}

                        {/* Main Content */}
                        {userRole === "Mentor" ? (
                            <Stack spacing={2.5}>
                                {/* Evaluation Management Section with Create Button */}
                                <EnhancedCard 
                                    title="Self-Reflection Management" 
                                    icon={<AnalyticsIcon />}
                                    delay={200}
                                >
                                    <Box sx={{ mb: 3 }}>
                                        <RatingForm handleRefresh={handleRefresh} />
                                    </Box>
                                    <Divider sx={{ my: 3 }} />
                                    <RatingFormsTable 
                                        key={refresh} 
                                        refresh={refresh}
                                        handleRefresh={handleRefresh} 
                                    />
                                </EnhancedCard>
                            </Stack>
                        ) : (
                            <Stack spacing={2.5}>
                                {/* Student Evaluations */}
                                <EnhancedCard 
                                    title="My Self-Reflections" 
                                    icon={<SelfReflectionIcon />}
                                    delay={200}
                                >
                                    <RatingFormsForUser />
                                </EnhancedCard>
                            </Stack>
                        )}
                    </Box>
                </Fade>
            </Container>
        </Box>
    );
};

export default Rating;